/**
 * Console copy — Traditional Chinese, Hong Kong usage, English technical nouns
 * kept literal (`run`, `step`, `token`, `webhook`, `schema`, `tag`, `cursor`).
 *
 * Mirrors `app/src/main/kotlin/io/blockway/infocollect/ui/Strings.kt` so the
 * phone and the console name the same thing the same way. Where the Android app
 * already has a term, reuse it verbatim rather than inventing a synonym.
 */

import type {
  DeviceHealth,
  DispatchDeviceState,
  RunStatus,
  RunTrigger,
  ScheduleType,
  ThinkingPolicy,
  VisionMode,
  Permission,
} from "@/types/fleet";

export const S = {
  appName: "InfoCollect Fleet",

  /* ---------------- generic ---------------- */
  save: "儲存",
  saving: "儲存中…",
  delete: "刪除",
  cancel: "取消",
  confirm: "確定",
  done: "完成",
  stop: "停止",
  back: "返回",
  edit: "編輯",
  search: "搜尋",
  close: "關閉",
  add: "新增",
  none: "無",
  all: "全部",
  enabled: "啟用",
  disabled: "停用",
  loading: "載入中…",
  empty: "沒有資料",
  advanced: "進階",
  defaultValue: "預設",
  error: "錯誤",
  retry: "重試",
  refresh: "重新整理",
  loadMore: "載入更多",
  noMore: "沒有更多了",
  copy: "複製",
  copied: "已複製",
  clear: "清除",
  apply: "套用",
  next: "下一步",
  previous: "上一步",
  ok: "OK",
  yes: "是",
  no: "否",
  unknown: "未知",

  /* ---------------- nav ---------------- */
  navGroupMonitor: "監控",
  navGroupWork: "作業",
  navGroupSystem: "系統",
  navOverview: "總覽",
  navDevices: "裝置",
  navRuns: "執行紀錄",
  navTasks: "任務",
  navDispatch: "派發",
  navFleetSettings: "Fleet 設定",
  navData: "擷取資料",
  navAlerts: "警報",
  navAudit: "稽核",
  signOut: "登出",
  commandMenu: "指令選單",

  /* ---------------- connection ---------------- */
  connOnline: "已連線",
  connConnecting: "連線中…",
  connOffline: "離線",
  connOfflineHint: "與伺服器失去連線，畫面顯示的是最後一次載入的資料。",
  staleData: "資料可能已過時",
  liveDisabledOffline: "離線期間無法使用即時操作",

  /* ---------------- login ---------------- */
  loginTitle: "登入 Fleet Console",
  loginSubtitle: "使用你的 operator 帳號登入。",
  loginUsername: "帳號",
  loginPassword: "密碼",
  loginSubmit: "登入",
  loginPending: "登入中…",
  loginFailed: "帳號或密碼不正確",
  loginNeedBoth: "請輸入帳號及密碼",
  loggedOut: "已登出",

  /* ---------------- permissions ---------------- */
  permissionDenied: "權限不足",
  noPermissionHint: "此操作需要更高權限，請聯絡 admin。",

  /* ---------------- overview ---------------- */
  overviewTitle: "總覽",
  overviewHint: "整個 fleet 的即時狀態。",
  statDevicesOnline: "裝置在線",
  statRunning: "執行中",
  statSuccessRateToday: "今日成功率",
  statTokensToday: "今日 tokens",
  failures24h: "過去 24 小時失敗",
  failures24hHint: "按 error 分組，點擊查看相關 run。",
  noFailures: "過去 24 小時沒有失敗的 run。",
  offlineDevices: "離線裝置",
  offlineDevicesHint: "最近失去連線的裝置。",
  noOfflineDevices: "所有裝置都在線。",
  viewAll: "查看全部",
  phase2ChartsTitle: "趨勢圖表",
  phase2ChartsHint: "執行量、成功率、token 用量的時間序列將在 phase 2 加入。",

  /* ---------------- devices ---------------- */
  devicesTitle: "裝置",
  devicesHint: "已登記的 Android 裝置。",
  colSelect: "選取",
  colDevice: "裝置",
  colHealth: "健康",
  colBattery: "電量",
  colStatus: "狀態",
  colTags: "標籤",
  colCurrentRun: "現時執行",
  colToday: "今日",
  colAppVersion: "App 版本",
  colLastSeen: "最後上線",
  filterSearch: "搜尋 label 或 ID…",
  filterHealth: "健康",
  filterTag: "標籤",
  filterVersion: "App 版本",
  filterHasRun: "執行中",
  filterHasRunYes: "只顯示執行中",
  filterHasRunNo: "只顯示閒置",
  clearFilters: "清除篩選",
  noDevices: "還沒有裝置。到「Fleet 設定」建立 enrollment token，再用手機掃描 QR code 登記。",
  noDevicesFiltered: "沒有符合篩選條件的裝置。試試放寬條件或清除篩選。",
  iconScreenOn: "螢幕開啟",
  iconScreenOff: "螢幕關閉",
  iconKeyguardLocked: "已鎖機",
  iconKeyguardUnlocked: "未鎖機",
  iconA11yOn: "無障礙服務已連接",
  iconA11yOff: "無障礙服務未連接",
  deviceDisarmed: "已解除武裝",
  deviceRevoked: "已撤銷",
  deviceUnderControl: "手動控制中",

  /* ---------------- bulk selection ---------------- */
  bulkSelectAllMatching: "選取全部符合條件的",
  bulkSelectAllMatchingUnknown: "選取全部符合條件的裝置",
  bulkClear: "清除選取",
  bulkDispatch: "派發任務",
  bulkTag: "加標籤",
  bulkDisarm: "解除武裝",
  bulkDevicesUnit: "部",

  /* ---------------- device detail ---------------- */
  deviceTabOverview: "概覽",
  deviceTabRuns: "執行紀錄",
  deviceTabSettings: "設定",
  deviceTabLogs: "日誌",
  deviceTabControl: "遙控",
  identity: "裝置資料",
  fieldLabel: "Label",
  fieldModel: "型號",
  fieldManufacturer: "製造商",
  fieldAndroid: "Android SDK",
  fieldAppVersion: "App 版本",
  fieldGroup: "群組",
  fieldTags: "標籤",
  fieldNotes: "備註",
  fieldEnrolledAt: "登記時間",
  fieldLastSeen: "最後上線",
  fieldNetwork: "網絡",
  fieldStorage: "可用儲存空間",
  fieldUptime: "開機時間",
  fieldPendingOutbox: "待上傳事件",
  fieldConfigVersion: "config version",
  fieldTaskSetVersion: "task set version",
  currentRun: "現時執行",
  lastRun: "最近一次執行",
  noCurrentRun: "目前閒置。",
  assignedTasks: "已指派任務",
  noAssignedTasks: "未指派任何任務。到「任務」頁把任務指派給這部裝置或它所屬的群組。",
  nextFire: "下次執行",
  settingsFleetManaged: "Fleet 管理",
  settingsFleetManagedHint: "由伺服器下發，裝置端不能改。",
  settingsDeviceLocal: "裝置本機",
  settingsDeviceLocalHint: "裝置自己擁有，fleet 永遠不會覆寫。",
  logsTitle: "裝置日誌",
  logsHint: "按需向裝置抓取最後 500 行。裝置離線時無法取得。",
  logsFetch: "抓取日誌",
  logsFetching: "抓取中…",
  logsOfflineError: "裝置離線，無法抓取日誌。",
  runNow: "立即執行",
  remoteControl: "遙控",
  remoteControlPhase2: "遙控功能將在 phase 2 推出。",
  labelSaved: "Label 已更新",

  /* ---------------- tasks ---------------- */
  tasksTitle: "任務",
  tasksHint: "任務定義推送到裝置後在本機執行。",
  newTask: "新增任務",
  editTask: "編輯任務",
  colTaskName: "名稱",
  colGoal: "目標",
  colSchedule: "排程",
  colAssignments: "已指派",
  colUpdated: "更新時間",
  noTasks: "還沒有任務。建立第一個任務，再派發到裝置。",
  deleteTaskConfirm: "確定刪除此任務？相關排程及指派會一併取消。",
  taskSaved: "任務已儲存",
  taskDeleted: "任務已刪除",

  sectionBasic: "基本",
  sectionPermissions: "權限",
  sectionAgent: "Agent",
  sectionExtraction: "擷取",
  sectionSchedule: "排程",
  sectionDelivery: "交付",

  fieldName: "名稱",
  fieldGoal: "目標（自然語言）",
  fieldGoalHint: "例如：打開天氣 App，記錄今日最高及最低氣溫",
  fieldEnabledTask: "啟用任務",
  fieldEnabledTaskHint: "停用後排程不會觸發，但仍可手動派發。",
  fieldAllowedApps: "允許使用的 App",
  allowedAppsAny: "任何 App（仍套用全域封鎖清單）",
  pickApps: "選擇 App",
  fieldAllowSend: "允許按「傳送 / 發佈」",
  fieldAllowSendDesc: "通訊及社交 App 內的 Send / Post / Tweet 按鈕",
  fieldBlockedRegex: "封鎖標籤 Regex（覆寫預設）",
  fieldBlockedRegexHint: "留空 = 使用預設（購買/轉帳/刪除…）",
  fieldRequireUnlock: "需要解鎖才執行",
  fieldRequireUnlockDesc: "日常機模式：鎖機時等待解鎖，而非略過",
  fieldVision: "Vision（截圖）",
  fieldThinking: "Thinking",
  fieldMaxSteps: "最多步數",
  fieldTimeout: "逾時（分鐘）",
  fieldSchema: "擷取資料 JSON Schema",
  fieldSchemaHint: "留空 = 任意 JSON object。驗證在伺服器進行。",
  fieldWebhook: "Webhook URL（覆寫全域）",
  fieldWebhookHint: "留空 = 使用 Fleet 設定的 webhook。",
  schemaValidating: "驗證中…",
  schemaValid: "Schema 有效",
  schemaColumns: "此 schema 會產生的欄位",
  schemaNoColumns: "此 schema 沒有定義具名欄位。",
  schemaLines: "行",
  nameRequired: "請輸入名稱",
  goalRequired: "請輸入目標",
  invalidTime: "時間格式須為 HH:mm",

  scheduleNone: "不排程",
  fieldInterval: "間隔（分鐘）",
  fieldTimes: "時間（HH:mm）",
  addTime: "新增時間",
  fieldWeekdays: "星期",
  nextThreeFires: "接下來 3 次執行",
  noFireTimes: "目前設定不會觸發任何執行。",
  fanoutWarning: "Fan-out 偏高",

  /* ---------------- dispatch ---------------- */
  dispatchTitle: "派發",
  dispatchHint: "把任務推到一批裝置上立即執行。",
  stepPickTask: "選任務",
  stepPickDevices: "選裝置",
  stepTiming: "時機",
  stepConfirm: "確認",
  dispatchExistingTask: "現有任務",
  dispatchAdhoc: "臨時目標（ad-hoc）",
  dispatchAdhocHint: "不會儲存成任務，只執行這一次。",
  dispatchPickTaskFirst: "請先選一個任務或輸入臨時目標。",
  tabIndividual: "個別",
  tabTags: "標籤",
  selectedDevices: "已選裝置",
  noDeviceSelected: "尚未選擇裝置。",
  timingImmediate: "立即執行",
  timingScheduledPhase2: "排程派發將在 phase 2 加入。",
  staggerTitle: "分批派發（stagger）",
  staggerHint: "同時喚醒太多裝置會令伺服器同一刻收到大量 run，分批可以攤平尖峰。",
  staggerBatch: "每批裝置數",
  staggerInterval: "每批間隔（秒）",
  staggerOff: "不分批",
  staggerAutoNote: "超過 20 部裝置時預設每 30 秒 10 部。",
  dispatchFire: "確認派發",
  dispatchPending: "派發中…",
  dispatchSent: "已派發",
  dispatchAckTitle: "派發進度",
  dispatchAckHint: "裝置逐部回報。離線裝置會在上線後執行。",
  dispatchCancel: "取消未開始的派發",
  dispatchCancelled: "已取消未開始的命令",

  /* ---------------- confirm impact ---------------- */
  impactAffected: "部裝置會收到這個 run",
  impactBreakdown: "分佈",
  impactOnline: "在線，會即時執行",
  impactStaleQueued: "stale，會排隊等下次上線",
  impactOfflineQueued: "offline 會排隊等上線",
  impactSkipped: "會被略過",
  impactSkipReasonControl: "正被手動控制，將略過",
  impactEstimated: "預計耗時",
  impactTypeToConfirm: "請輸入受影響裝置數目以確認",
  impactTypeToConfirmHint: "這個操作無法復原。",
  impactMismatch: "數字不符",

  /* ---------------- runs ---------------- */
  runsTitle: "執行紀錄",
  runsHint: "整個 fleet 的 run。",
  colRun: "Run",
  colTask: "任務",
  colTrigger: "觸發",
  colSteps: "步數",
  colTokens: "tokens",
  colDuration: "時長",
  colStarted: "開始時間",
  colError: "錯誤",
  filterStatus: "狀態",
  filterDevice: "裝置",
  filterTask: "任務",
  filterDate: "時間",
  filterHasError: "只顯示有錯誤",
  dateToday: "今日",
  date24h: "過去 24 小時",
  date7d: "過去 7 日",
  date30d: "過去 30 日",
  dateAll: "全部",
  noRuns: "還沒有 run。到「派發」頁把任務推到裝置上執行。",
  noRunsFiltered: "沒有符合條件的 run。",

  runDetailTitle: "執行詳情",
  goal: "目標",
  trigger: "觸發",
  steps: "步數",
  tokens: "tokens",
  duration: "時長",
  summary: "摘要",
  extractions: "擷取資料",
  dataTitle: "擷取資料",
  assistantTitle: "用 AI 寫這個任務",
  assistantHint: "用平時講嘢嘅方式寫低你想部機做乜，AI 會幫你填好下面嘅設定。",
  assistantPlaceholder: "例如：每朝八點喺 HKTVmall 搜尋「牛奶」，記低頭 10 件貨嘅名、價錢同折扣。",
  assistantGenerate: "生成設定",
  assistantImprove: "改善現有設定",
  assistantWorking: "AI 撰寫中…",
  assistantApplied: "已填入，請檢查後再儲存",
  assistantNotes: "AI 的假設",
  assistantIssues: "仍需修正",
  assistantUnknownApps: "以下 App 套件名喺機隊上搵唔到，請用「允許的應用程式」揀返正確嗰個",
  assistantUnavailable: "未設定模型端點或 API key，請先到 Fleet 設定填寫。",
  sectionVariables: "任務變數",
  variablesHint: "目標入面嘅 {{變數}}。可以喺呢度填預設值，派發時亦可以逐次覆寫。",
  variableDefault: "預設值",
  variablesUnused: "AI 提及但目標未用到的變數",
  fillVariables: "填寫任務變數",
  fillVariablesHint: "呢個任務有未填嘅變數，填好先可以派發。",
  variableRequired: "必填",
  dataHint: "任務執行時擷取到的每一筆資料。",
  noData: "未有擷取資料",
  noDataBody: "任務成功執行並呼叫 extract 之後，資料會在這裡出現。",
  noDataFiltered: "沒有符合條件的資料",
  colCreated: "擷取時間",
  recordDetail: "資料內容",
  viewRun: "查看執行紀錄",
  exportCsv: "匯出 CSV",
  exportJson: "匯出 JSON",
  exporting: "匯出中…",
  exportFailed: "匯出失敗",
  searchData: "搜尋內容",
  fieldsHidden: "另有欄位",
  timeline: "步驟",
  observation: "畫面觀察",
  reasoning: "推理",
  thinkingBadge: "thinking",
  latency: "延遲",
  ttft: "TTFT",
  showMore: "展開",
  showLess: "收起",
  stopRun: "停止 run",
  stopRunConfirm: "確定停止這個 run？裝置會在目前這一步之後停下。",
  stopRunSent: "已送出停止指令",
  onlyChanges: "只顯示變化",
  onlyChangesHint: "與上一步比較，未變的行會變淡。",
  waitingForSteps: "等待 Agent 開始…",
  autoScrollPaused: "已暫停自動捲動",
  newStepsPending: "個新 step",
  screenshot: "截圖",
  noScreenshot: "此步沒有截圖。",
  toolResultOk: "成功",
  toolResultFail: "失敗",
  liveTail: "即時",

  /* ---------------- fleet settings ---------------- */
  fleetSettingsTitle: "Fleet 設定",
  fleetSettingsHint: "伺服器下發到所有裝置的設定。Phase 1 為唯讀。",
  fleetSettingsReadOnly: "唯讀",
  fleetSettingsReadOnlyHint: "編輯功能將在 phase 2 開放；目前請直接在伺服器修改。",
  sectionLimits: "限制",
  fieldMaxConcurrentRuns: "同時最多 run",
  fieldDefaultStagger: "預設 stagger（毫秒）",
  sectionRetention: "保留",
  fieldKeepScreenshots: "保留截圖天數",
  fieldRetentionDays: "資料保留天數",
  fieldDiskUsage: "磁碟用量",
  sectionSecrets: "密鑰",
  fieldAgensKey: "Agens API key",
  keyFingerprintOnly: "只顯示 fingerprint，密鑰不會回傳。",
  keyNotSet: "未設定",

  /* ---------------- phase 2 stubs ---------------- */
  phase2Title: "Phase 2",
  phase2Body: "這個功能仍在開發中，會在 phase 2 推出。",
  phase2Control: "即時畫面與遙控會在 phase 2 推出，屆時可直接在瀏覽器點按裝置畫面。",
  phase2Alerts: "警報規則會在 phase 2 推出。",
  phase2Audit: "稽核紀錄會在 phase 2 推出。",

  /* ---------------- errors / states ---------------- */
  queryFailedTitle: "載入失敗",
  mutationFailed: "操作失敗",
  deviceOfflineQueued: "指令已排隊，裝置上線後執行",
  notFoundTitle: "找不到",
  notFoundBody: "這個頁面不存在。",
} as const;

/* ------------------------------------------------------------------ *
 * Enum labels
 * ------------------------------------------------------------------ */

export function runStatusLabel(s: RunStatus): string {
  switch (s) {
    case "QUEUED":
      return "排隊中";
    case "WAITING_UNLOCK":
      return "等待解鎖";
    case "RUNNING":
      return "執行中";
    case "SUCCESS":
      return "成功";
    case "FAILED":
      return "失敗";
    case "SKIPPED":
      return "已略過";
    case "CANCELLED":
      return "已取消";
  }
}

export function runTriggerLabel(t: RunTrigger): string {
  // Three triggers, not two. A REMOTE run was dispatched from this console; calling it 排程 sends
  // an operator looking for a schedule that does not exist.
  switch (t) {
    case "MANUAL":
      return "手動";
    case "SCHEDULE":
      return "排程";
    case "REMOTE":
      return "遠端派發";
  }
}

export function scheduleTypeLabel(t: ScheduleType): string {
  switch (t) {
    case "NONE":
      return "不排程";
    case "INTERVAL":
      return "每隔";
    case "DAILY":
      return "每日";
    case "WEEKLY":
      return "每週";
  }
}

export function visionModeLabel(v: VisionMode): string {
  switch (v) {
    case "OFF":
      return "關閉";
    case "AUTO":
      return "自動";
    case "ALWAYS":
      return "永遠";
  }
}

export function thinkingPolicyLabel(p: ThinkingPolicy): string {
  switch (p) {
    case "ADAPTIVE":
      return "自適應";
    case "ALWAYS_ON":
      return "永遠開";
    case "ALWAYS_OFF":
      return "永遠關";
  }
}

export function healthLabel(h: DeviceHealth): string {
  switch (h) {
    case "online":
      return "online";
    case "stale":
      return "stale";
    case "offline":
      return "offline";
    case "unseen":
      return "unseen";
  }
}

export function healthDescription(h: DeviceHealth): string {
  switch (h) {
    case "online":
      return "socket 已連接，或 5 分鐘內有心跳";
    case "stale":
      return "24 小時內見過，但現在沒有連線";
    case "offline":
      return "超過 24 小時沒有上線";
    case "unseen":
      return "已登記但從未上線";
  }
}

export function dispatchStateLabel(s: DispatchDeviceState): string {
  switch (s) {
    case "queued":
      return "排隊中";
    case "sent":
      return "已送出";
    case "running":
      return "執行中";
    case "success":
      return "成功";
    case "failed":
      return "失敗";
    case "expired":
      return "已逾時";
    case "skipped":
      return "已略過";
  }
}

/** Which role an operator needs, shown in a disabled control's tooltip. */
export function permissionHint(p: Permission): string {
  switch (p) {
    case "read":
      return "任何角色";
    case "dispatch_run":
    case "stop_run":
    case "edit_task":
    case "manage_device":
      return "operator 或 admin";
    case "remote_control":
    case "enroll_device":
    case "export_data":
      return "operator 或 admin";
    case "danger_zone":
      return "admin";
  }
}

/** The `skipped[].reason` codes rest-api.md returns from dispatch. */
export function skipReasonLabel(reason: string): string {
  switch (reason) {
    case "device_under_manual_control":
      return S.impactSkipReasonControl;
    case "device_disarmed":
      return "已解除武裝，將略過";
    case "device_revoked":
      return "已撤銷，將略過";
    case "run_in_progress":
      return "已有 run 在執行，將略過";
    default:
      return reason;
  }
}

/* ------------------------------------------------------------------ *
 * Interpolated copy
 * ------------------------------------------------------------------ */

export const T = {
  onlineBadge: (online: number, total: number) => `在線 ${online}/${total}`,
  deviceHint: (stale: number, offline: number) => `${stale} stale · ${offline} offline`,
  todayCounts: (success: number, failed: number) => `${success} ✓ / ${failed} ✗`,
  stepProgress: (n: number, max: number) => `step ${n}/${max}`,
  selectedOnPage: (n: number) => `已選 ${n} 部（本頁）`,
  selectAllMatching: (n: number) => `選取全部符合條件的 ${n} 部`,
  selectedAllMatching: (n: number) => `已選取全部符合條件的 ${n} 部`,
  failureGroup: (count: number, code: string) => `${count}× ${code}`,
  failureAffects: (devices: number) => `影響 ${devices} 部機`,
  failureTask: (task: string) => `任務「${task}」`,
  fanout: (devices: number, per: string, runsPerHour: number) =>
    `${devices} 部裝置 × ${per} = ${runsPerHour} 次執行/小時`,
  everyMinutes: (m: number) => `每 ${m} 分鐘`,
  perDay: (n: number) => `每日 ${n} 次`,
  affectedCount: (n: number) => `${n} 部裝置`,
  queuedOffline: (n: number) => `${n} 部 offline 會排隊等上線`,
  queuedStale: (n: number) => `${n} 部 stale 會排隊等上線`,
  skippedControl: (n: number) => `${n} 部正被手動控制，將略過`,
  skippedOther: (n: number, reason: string) => `${n} 部${reason}`,
  newSteps: (n: number) => `${n} 個新 step`,
  httpStatus: (status: number, code: string) => `HTTP ${status} · ${code}`,
  logsTail: (n: number) => `最後 ${n} 行`,
  nDevices: (n: number) => `${n} 部裝置`,
  nRuns: (n: number) => `${n} 個 run`,
  nTasks: (n: number) => `${n} 個任務`,
  nErrors: (n: number) => `${n} 個錯誤`,
  runOf: (task: string, device: string) => `${task} · ${device}`,
  batchOf: (size: number, seconds: number) => `每 ${seconds} 秒 ${size} 部`,
  assignmentCount: (n: number) => (n === 0 ? "未指派" : `${n} 個指派`),
  extractionCount: (n: number) => `${n} 筆擷取資料`,
  diskUsage: (used: string, total: string) => `${used} / ${total}`,
} as const;
